import { inject, Injectable } from '@angular/core';
import { ApiService } from '../../../services/api.service';
import {
    CreateTemplateRequest,
    PreviewTemplateRequest,
    RejectTemplateRequest,
    ShowreportTemplateDto,
    TemplateRevisionInfo,
    UpdateTemplateRequest
} from './templates-admin.model';

/**
 * Service for managing showreport templates.
 * Handles all API communication for template CRUD and workflow operations.
 */
@Injectable({
    providedIn: 'root'
})
export class TemplatesAdminService {
    private readonly api: ApiService = inject(ApiService);
    private readonly basePath = 'templates';

    // Cache for hasCrewFields check to avoid repeated API calls
    private crewFieldsCache: Map<number, boolean> = new Map();

    // Cache for active templates to avoid repeated API calls on dashboard
    private activeTemplateCache: Map<number, ShowreportTemplateDto | null> = new Map();
    private activeTemplatePendingRequests: Map<number, Promise<ShowreportTemplateDto | null>> = new Map();

    /**
     * Get all templates for a specific show.
     */
    public async getTemplatesForShow(showId: number): Promise<ShowreportTemplateDto[]> {
        return await this.api.getAll<ShowreportTemplateDto>(`${this.basePath}?showId=${showId}`);
    }

    /**
     * Get a single template by ID.
     */
    public async getTemplateById(id: number): Promise<ShowreportTemplateDto> {
        return await this.api.getById<ShowreportTemplateDto>(this.basePath, id);
    }

    /**
     * Get the active template for a show.
     * Results are cached to avoid repeated API calls (e.g., from multiple performance cards).
     */
    public async getActiveTemplateForShow(showId: number): Promise<ShowreportTemplateDto | null> {
        // Return cached result if available
        if (this.activeTemplateCache.has(showId)) {
            return this.activeTemplateCache.get(showId);
        }

        // If a request is already pending for this showId, wait for it
        if (this.activeTemplatePendingRequests.has(showId)) {
            return this.activeTemplatePendingRequests.get(showId);
        }

        // Create the request and store it to prevent duplicate calls
        const request = this.fetchActiveTemplate(showId);
        this.activeTemplatePendingRequests.set(showId, request);

        try {
            const result = await request;
            this.activeTemplateCache.set(showId, result);
            return result;
        } finally {
            this.activeTemplatePendingRequests.delete(showId);
        }
    }

    private async fetchActiveTemplate(showId: number): Promise<ShowreportTemplateDto | null> {
        try {
            const result = await this.api.get<ShowreportTemplateDto>(`${this.basePath}/active/${showId}`);
            return Array.isArray(result) ? result[0] : result;
        } catch (error) {
            // No active template is not an error
            return null;
        }
    }

    /**
     * Clear the active template cache (e.g., when a template is updated).
     */
    public clearActiveTemplateCache(showId?: number): void {
        if (showId !== undefined) {
            this.activeTemplateCache.delete(showId);
        } else {
            this.activeTemplateCache.clear();
        }
    }

    /**
     * Create a new template.
     */
    public async createTemplate(showId: number, request: CreateTemplateRequest): Promise<ShowreportTemplateDto> {
        const result = await this.api.post<ShowreportTemplateDto>(`${this.basePath}?showId=${showId}`, request);
        return Array.isArray(result) ? result[0] : result as unknown as ShowreportTemplateDto;
    }

    /**
     * Update an existing template.
     */
    public async updateTemplate(id: number, request: UpdateTemplateRequest): Promise<ShowreportTemplateDto> {
        return await this.api.put<ShowreportTemplateDto>(`${this.basePath}/${id}`, request);
    }

    /**
     * Delete a template (only drafts can be deleted).
     */
    public async deleteTemplate(id: number): Promise<void> {
        await this.api.delete(`${this.basePath}/${id}`);
    }

    /**
     * Submit a template for approval.
     */
    public async submitForApproval(id: number): Promise<ShowreportTemplateDto> {
        const result = await this.api.postRequest(`${this.basePath}/${id}/submit`, {});
        return result?.data || result;
    }

    /**
     * Approve a template (makes it active).
     */
    public async approveTemplate(id: number): Promise<ShowreportTemplateDto> {
        const result = await this.api.postRequest(`${this.basePath}/${id}/approve`, {});
        return result?.data || result;
    }

    /**
     * Reject a template (returns it to draft status).
     */
    public async rejectTemplate(id: number, request: RejectTemplateRequest): Promise<ShowreportTemplateDto> {
        const result = await this.api.postRequest(`${this.basePath}/${id}/reject`, request);
        return result?.data || result;
    }

    /**
     * Get the version history for a template.
     */
    public async getTemplateHistory(id: number): Promise<TemplateRevisionInfo[]> {
        return await this.api.get<TemplateRevisionInfo[]>(`${this.basePath}/${id}/versions`);
    }

    /**
     * Duplicate an existing template as a new draft.
     */
    public async duplicateTemplate(id: number, showId: number, newName: string): Promise<ShowreportTemplateDto> {
        // First get the existing template
        const existing = await this.getTemplateById(id);

        // Create a new template with the same sections/fields
        const request: CreateTemplateRequest = {
            name: newName,
            logoUrl: existing.logoUrl,
            primaryColor: existing.primaryColor,
            secondaryColor: existing.secondaryColor,
            // Use sections if available, otherwise fall back to legacy fields
            sections: existing.sections?.map(section => ({
                ...section,
                id: undefined, // Remove IDs for new entities
                fields: section.fields?.map(field => ({
                    ...field,
                    id: undefined
                }))
            })),
            fields: !existing.sections?.length ? existing.fields?.map(field => ({
                ...field,
                id: undefined // Remove IDs for new entities
            })) : undefined
        };

        return await this.createTemplate(showId, request);
    }

    /**
     * Check if a show's active template has crew fields (USER_SELECT or CREW_MEMBER type).
     * Also checks for CREW sections which indicate crew should be displayed.
     * Results are cached to avoid repeated API calls.
     */
    public async hasCrewFields(showId: number): Promise<boolean> {
        // Check cache first
        if (this.crewFieldsCache.has(showId)) {
            return this.crewFieldsCache.get(showId);
        }

        try {
            const template = await this.getActiveTemplateForShow(showId);
            if (!template) {
                this.crewFieldsCache.set(showId, false);
                return false;
            }

            // Check if template has a CREW section
            const hasCrewSection = template.sections?.some(
                section => section.sectionType === 'CREW'
            ) ?? false;

            if (hasCrewSection) {
                this.crewFieldsCache.set(showId, true);
                return true;
            }

            // Check top-level fields (legacy structure)
            const hasTopLevelCrewFields = template.fields?.some(
                field => field.fieldType === 'USER_SELECT' || field.fieldType === 'CREW_MEMBER'
            ) ?? false;

            if (hasTopLevelCrewFields) {
                this.crewFieldsCache.set(showId, true);
                return true;
            }

            // Check fields inside sections (new structure)
            const hasSectionCrewFields = template.sections?.some(
                section => section.fields?.some(
                    field => field.fieldType === 'USER_SELECT' || field.fieldType === 'CREW_MEMBER'
                )
            ) ?? false;

            this.crewFieldsCache.set(showId, hasSectionCrewFields);
            return hasSectionCrewFields;
        } catch (error) {
            console.error('[TemplatesAdminService] Error checking crew fields:', error);
            // Default to false if there's an error
            this.crewFieldsCache.set(showId, false);
            return false;
        }
    }

    /**
     * Clear the crew fields cache (e.g., when a template is updated).
     */
    public clearCrewFieldsCache(showId?: number): void {
        if (showId !== undefined) {
            this.crewFieldsCache.delete(showId);
        } else {
            this.crewFieldsCache.clear();
        }
    }

    /**
     * Generate a PDF preview of a template with mock data.
     * Does not require the template to be saved.
     */
    public async previewTemplate(request: PreviewTemplateRequest): Promise<Blob> {
        return await this.api.postBlob(`${this.basePath}/preview`, request);
    }
}
